import hexToRgb from "./hexToRgb";
import { resolveColorScheme, deriveDarkColors, surfaceTokens } from "./colorScheme";

// Verilen zemin uzerinde okunabilir yazi rengi (WCAG bagil parlaklik).
const readableOn = (background, light = "#ffffff", dark = "#1f2937") => {
    if (typeof background !== "string") return light;
    let hex = background.trim().replace("#", "");
    if (hex.length === 3) {
        hex = hex
            .split("")
            .map((c) => c + c)
            .join("");
    }
    if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) return light;
    const channel = (value) => {
        const c = parseInt(value, 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    const luminance =
        0.2126 * channel(hex.slice(0, 2)) + 0.7152 * channel(hex.slice(2, 4)) + 0.0722 * channel(hex.slice(4, 6));
    // Beyazin kontrasti koyu murekkepten iyiyse beyaz kalsin.
    const contrastWithLight = 1.05 / (luminance + 0.05);
    const darkLuminance = 0.0356;
    const contrastWithDark = (luminance + 0.05) / (darkLuminance + 0.05);
    return contrastWithLight >= contrastWithDark ? light : dark;
};

// WCAG bagil parlaklik; gecersiz/saydam renkte null
const luminanceOf = (color) => {
    if (typeof color !== "string") return null;
    let hex = color.trim().replace("#", "");
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    if (hex.length === 8) hex = hex.slice(0, 6);
    if (hex.length !== 6 || /[^0-9a-fA-F]/.test(hex)) return null;
    const channel = (value) => {
        const c = parseInt(value, 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(hex.slice(0, 2)) + 0.7152 * channel(hex.slice(2, 4)) + 0.0722 * channel(hex.slice(4, 6));
};

const contrastOf = (a, b) => {
    const la = luminanceOf(a);
    const lb = luminanceOf(b);
    if (la === null || lb === null) return null;
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Ozel ya da eski kaydedilmis temalarda yazi zeminle neredeyse ayni renk olabiliyor (kirmizi uzerine
 * pembe gibi). Okunmayacak kadar dusukse (WCAG 3:1 alti) yazi, zemine gore beyaz ya da koyu
 * murekkebe cekilir; okunur renklere dokunulmaz.
 */
const ensureReadable = (text, background, min = 3) => {
    const ratio = contrastOf(text, background);
    return ratio !== null && ratio < min ? readableOn(background) : text;
};

const setupV2Theme = (themeConfig, colorScheme = "light") => {
    if (themeConfig) {
        const { fonts } = themeConfig;
        const prefersDark = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)").matches : false;
        const mode = resolveColorScheme(colorScheme, prefersDark);
        const colors = mode === "dark" ? deriveDarkColors(themeConfig.colors) : themeConfig.colors;
        const {
            headerColorFirst, // Hader Section Color
            headerColorSecond, // Header Section Color 2
            backgroundColorFirst, // Background Color 1
            textColor, // Text Color
            errorColor, // error-color
            backgroundColorSecond, // Background Color 2
            formColor, //Form color
            representativeBubbleColor, //Representative bubble color
            representativeTextColor, //Representative text color
            customerBubbleColor, //Customer bubble color
            customerTextColor, //Customer text color
            buttonColor, //Button color
            headerTextColor, //Header text color
            formColorText, //Form color text
            chatBotButtonText, //Chatbot button text color
            chatBotButtonColor, //Chatbot button color
        } = colors;

        var themeParameterConfigMap = [];

        // Sohbet zemini background-color-first -> second gecisi (ChatWindow); okunurluk iki uca birden bakar.
        // Ozel/eski temalarda balon zeminle ayni renkse kayboluyor, yazi zemine yakinsa okunmuyordu.
        const surfaces = [backgroundColorFirst, backgroundColorSecond].filter((c) => luminanceOf(c) !== null);
        if (!surfaces.length) surfaces.push("#ffffff");
        // Gecisin iki ucu birbirine cok zitsa (koyudan aciga) hicbir yazi ikisinde birden okunmuyor: zemin duzlesir
        const readableOnBoth = (list) => ["#ffffff", "#111827"].some((c) => list.every((bg) => (contrastOf(c, bg) || 0) >= 3));
        if (surfaces.length > 1 && !readableOnBoth(surfaces)) surfaces.splice(1);
        const bgSecond = surfaces[surfaces.length - 1];
        const minContrast = (color) => {
            const ratios = surfaces.map((bg) => contrastOf(color, bg)).filter((r) => r !== null);
            return ratios.length ? Math.min.apply(null, ratios) : null;
        };
        const bestOf = (candidates) => candidates.reduce((best, c) => ((minContrast(c) || 0) > (minContrast(best) || 0) ? c : best));
        const readableOnChat = (text, min = 3) => {
            const ratio = minContrast(text);
            return ratio !== null && ratio < min ? bestOf(["#ffffff", "#1f2937", "#111827"]) : text;
        };
        const indistinct = (color, min) => {
            const ratio = minContrast(color);
            return ratio !== null && ratio < min;
        };
        const surfaceLum = surfaces.reduce((sum, c) => sum + luminanceOf(c), 0) / surfaces.length;
        // Acik zeminde hazir temalarin temsilci balonu bilerek cok acik bir ton (golgesiyle ayrilir); yalniz
        // zeminle birebir ayniysa degisir. Renkli/koyu zeminde balon zemine yakinsa beyaz balon olur.
        const agentBubble =
            surfaceLum > 0.6
                ? indistinct(representativeBubbleColor, 1.02)
                    ? surfaces.some((c) => luminanceOf(c) > 0.95) ? "#eef0f3" : "#ffffff"
                    : representativeBubbleColor
                : indistinct(representativeBubbleColor, 1.08)
                    ? "#ffffff"
                    : representativeBubbleColor;
        // ziyaretci balonu temsilciden ayrilsin: zemin aciksa koyu, koyuysa beyaz balon
        let visitorBubble = customerBubbleColor;
        if (indistinct(visitorBubble, 1.3)) visitorBubble = indistinct(buttonColor, 1.3) ? bestOf(["#1f2937", "#ffffff"]) : buttonColor;

        themeParameterConfigMap["header-color"] = headerColorFirst;
        themeParameterConfigMap["header-color-second"] = headerColorSecond;
        themeParameterConfigMap["background-color-first"] = backgroundColorFirst;
        themeParameterConfigMap["background-color-second"] = bgSecond;
        themeParameterConfigMap["primary_"] = headerColorFirst;
        themeParameterConfigMap["primary-text"] = readableOnChat(textColor);
        themeParameterConfigMap["secondary-text"] = customerTextColor;
        themeParameterConfigMap["chat-bubble-readonly-text"] = readableOnChat(textColor);
        themeParameterConfigMap["chat-bubble-text-bg"] = backgroundColorFirst;
        themeParameterConfigMap["agent-bubble-color"] = agentBubble;
        themeParameterConfigMap["agent-bubble-text-color"] = ensureReadable(representativeTextColor, agentBubble);
        themeParameterConfigMap["customer-bubble-color"] = visitorBubble;
        themeParameterConfigMap["customer-text-color"] = ensureReadable(customerTextColor, visitorBubble);
        // Saat damgasi balonun disinda, kart zemininde duruyor: gonderilen mesaj
        // metnine baglaninca acik temalarda beyaz uzerine beyaz kaliyordu.
        // once yumusak tonlar; gri gibi orta zeminde yetmezse tam beyaz/koyu
        const softTime = bestOf(["#f3f4f6", "#4b5563"]);
        themeParameterConfigMap["message-time"] = (minContrast(softTime) || 0) >= 3 ? softTime : bestOf(["#ffffff", "#111827"]);
        themeParameterConfigMap["footer-text-color"] = ensureReadable(customerTextColor, backgroundColorFirst);
        themeParameterConfigMap["footer-bg"] = backgroundColorFirst;
        // yazi kutusu her temada temiz bir yuzey; zeminle ayni renk olunca kutu kayboluyordu
        themeParameterConfigMap["chat-input-bg"] = mode === "dark" ? surfaceTokens(mode)["sps-surface-raised"] : "#ffffff";
        themeParameterConfigMap["chat-input-ph"] = "#a1a8a8";
        themeParameterConfigMap["chat-input-icon"] = "#A2A8A8";
        themeParameterConfigMap["color-bt"] = "#E4E8ED";
        themeParameterConfigMap["form-input-label"] = readableOnChat(formColorText);
        themeParameterConfigMap["form-input-border"] = buttonColor;
        themeParameterConfigMap["form-input-text"] = readableOnChat(formColorText);
        themeParameterConfigMap["lang-list-primary"] = backgroundColorFirst;
        themeParameterConfigMap["button-color"] = buttonColor;
        // Buton etiketi sabit beyaz olamaz: acik marka renklerinde okunmuyor.
        // Zemin parlakligina gore beyaz ya da koyu murekkep seciliyor.
        themeParameterConfigMap["button-text-color"] = readableOn(buttonColor);
        themeParameterConfigMap["header-text-color"] = ensureReadable(headerTextColor, headerColorFirst);
        themeParameterConfigMap["form-color-text"] = readableOnChat(formColorText);
        themeParameterConfigMap["error-color"] = errorColor;
        themeParameterConfigMap["lang-list-secondary"] = hexToRgb(backgroundColorFirst, 5);
        themeParameterConfigMap["translate-received"] = hexToRgb(backgroundColorFirst, 22);
        themeParameterConfigMap["scrollbar-color"] = hexToRgb(backgroundColorFirst, 22);
        themeParameterConfigMap["scrollbar-color"] = headerColorFirst + "75";
        themeParameterConfigMap["main-text-color"] = "white";
        // chatbot secenekleri: zemin saydamsa yazi sohbet zemininde durur, oradaki okunurluga bakilir
        const chatbotOnSurface = luminanceOf(chatBotButtonColor) === null;
        themeParameterConfigMap["chatbot-button-text-color"] = chatbotOnSurface ? readableOnChat(chatBotButtonText) : ensureReadable(chatBotButtonText, chatBotButtonColor);
        themeParameterConfigMap["chatbot-button-color"] = chatBotButtonColor;
        if (fonts && fonts.fontCustom) {
            themeParameterConfigMap["theme-font"] = fonts.fontCustom;
        } else {
            themeParameterConfigMap["theme-font"] = "Poppins";
        }
        Object.assign(themeParameterConfigMap, surfaceTokens(mode));
        document.documentElement.setAttribute("data-color-scheme", mode);
        setCSSParameters(themeParameterConfigMap);
    }
};

const setCSSParameters = (parameterConfigMap) => {
    const r = document.querySelector(":root");
    Object.keys(parameterConfigMap).forEach((key) => {
        r.style.setProperty(`--${key}`, parameterConfigMap[key]);
    });
};

export { contrastOf, ensureReadable };
export default setupV2Theme;



// WEBPACK FOOTER //
// ./src/utils/theme/setupV2Theme.js